import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import BASE_URL from "../../../config/apiConfig";

const NotificationPage = () => {
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    body: "",
    imageUrl: "",
    name: "",
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch devices
  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/get-all-devices`);
      setDevices(response.data);
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allDeviceTokens = devices.map((device) => device.token);
      setSelectedDevices(allDeviceTokens);
    } else {
      setSelectedDevices([]);
    }
  };

  const handleSelectOne = (token) => {
    setSelectedDevices((prevSelected) =>
      prevSelected.includes(token)
        ? prevSelected.filter((deviceToken) => deviceToken !== token)
        : [...prevSelected, token]
    );
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.body) {
      setErrorMessage("Title and body are required.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/api/admin/notifications/send-notifications`,
        {
          title: formData.title,
          body: formData.body,
          imageUrl: formData.imageUrl,
          name: formData.name,
          recipients: selectedDevices,
        }
      );
      setSuccessMessage("Notification sent successfully!");
      setSelectedDevices([]);
      setFormData({ title: "", body: "", imageUrl: "", name: "" });
    } catch (error) {
      setErrorMessage("Failed to send notifications.");
      console.error("Error sending notification:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, backgroundColor: "#f0f4f8", minHeight: "100vh" }}>
      <Typography
        variant="h4"
        sx={{
          mb: 4,
          fontWeight: "bold",
          color: "#2c3e50",
          textAlign: "center",
        }}
      >
        Send Notifications
      </Typography>

      {/* Device Table */}
      <TableContainer
        component={Paper}
        sx={{
          mb: 4,
          borderRadius: 2,
          boxShadow: 3,
          background: "linear-gradient(135deg, #f7f9fc, #e3eaf1)",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}>
                <Checkbox
                  checked={
                    selectedDevices.length === devices.length &&
                    devices.length > 0
                  }
                  onChange={handleSelectAll}
                  sx={{ color: "#3498db" }}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "#2980b9" }}>
                Email
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "#2980b9" }}>
                Notification Token
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map((device) => (
              <TableRow key={device.token}>
                <TableCell>
                  <Checkbox
                    checked={selectedDevices.includes(device.token)}
                    onChange={() => handleSelectOne(device.token)}
                    sx={{ color: "#3498db" }}
                  />
                </TableCell>
                <TableCell sx={{ color: "#34495e" }}>{device.email}</TableCell>
                <TableCell sx={{ color: "#34495e" }}>{device.token}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Notification Form */}
      <Paper
        sx={{
          p: 4,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
          maxWidth: 600,
          margin: "0 auto",
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 3, fontWeight: "bold", color: "#2c3e50" }}
        >
          Compose Notification
        </Typography>
        <Box component="form" autoComplete="off">
          <TextField
            label="Notification Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            fullWidth
            required
            sx={{
              mb: 2,
              backgroundColor: "#f7f9fc",
              borderRadius: 2,
              "& .MuiInputBase-root": {
                borderRadius: 2,
              },
            }}
          />
          <TextField
            label="Notification Body"
            name="body"
            value={formData.body}
            onChange={handleInputChange}
            fullWidth
            required
            multiline
            rows={4}
            sx={{
              mb: 2,
              backgroundColor: "#f7f9fc",
              borderRadius: 2,
              "& .MuiInputBase-root": {
                borderRadius: 2,
              },
            }}
          />
          <TextField
            label="Notification Image URL (optional)"
            name="imageUrl"
            value={formData.imageUrl}
            onChange={handleInputChange}
            fullWidth
            sx={{
              mb: 2,
              backgroundColor: "#f7f9fc",
              borderRadius: 2,
              "& .MuiInputBase-root": {
                borderRadius: 2,
              },
            }}
          />
          <TextField
            label="Notification Name (optional)"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            sx={{
              mb: 3,
              backgroundColor: "#f7f9fc",
              borderRadius: 2,
              "& .MuiInputBase-root": {
                borderRadius: 2,
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              mt: 2,
              padding: "10px 20px",
              fontWeight: "bold",
              textTransform: "uppercase",
              backgroundColor: "#3498db",
              "&:hover": {
                backgroundColor: "#2980b9",
              },
            }}
          >
            {loading ? "Sending..." : "Send Notification"}
          </Button>
        </Box>
      </Paper>

      {/* Snackbars */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage("")}
      >
        <Alert onClose={() => setSuccessMessage("")} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage("")}
      >
        <Alert onClose={() => setErrorMessage("")} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationPage;
